import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import apiClient from "../utils/apiClient";
import styled from "@emotion/styled";
import { Room, Track, LocalVideoTrack } from "livekit-client";
import { client, urlFor } from "../sanity/sanityClient";
import { getFollowersList } from "../services/feed";
import { host } from "../utils/APIRoutes";
import { FiX, FiRadio, FiSend, FiGift, FiPackage, FiSliders, FiRefreshCw } from "react-icons/fi";
import { AiOutlineEye, AiFillHeart } from "react-icons/ai";
import { useBeautifyCamera } from "../hooks/useBeautifyCamera";
import FilterSelector from "./FilterSelector";

/**
 * Go Live Control Panel — the seller-facing dashboard for hosting a
 * Bridgen Live session. Two modes:
 *  - "setup": create a new session (title, cover image, currency,
 *    quick replies) before going live.
 *  - "live": the actual hosting dashboard — real-time chat feed with
 *    reply input, viewer/heart counts, product pinning from the
 *    host's own catalog, and surprise-unboxing timer control.
 *
 * Emits the same socket events Live.js (the viewer-facing component)
 * already listens for: live-chat-message, live-pin-product,
 * live-surprise-update, live-end-session. See live_socket_server.js
 * and live_socket_server_additions_v2.js for the server side.
 */

const PRODUCT_TYPES = [
  "clothingandfashion", "electronics", "arts", "fashion", "salon", "grocery",
  "beauty", "crops", "energyandfuel", "cuisines", "booksandstationery",
  "sports", "kitchen", "hotels", "homeandfurniture", "hardware", "books",
  "estate", "foodandbeverages", "agritech", "giftoptions",
  "servicesandinstitutions", "homeandfurnitures", "livestock", "vehicles",
  "jewelryandaccessories", "medical", "useditems", "construction",
  "kidsandtoys", "eventsandnightclubs", "hospitality",
];

const AUTO_END_DURATION_MS = 60 * 60 * 1000; // 1 hour — live streams end automatically after this

const formatCountdown = (ms) => {
  if (ms === null || ms <= 0) return "00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    : `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const CURRENCY_OPTIONS = [
  { code: "TZS", name: "Tanzanian Shilling (TZS)" },
  { code: "KES", name: "Kenyan Shilling (KES)" },
  { code: "UGX", name: "Ugandan Shilling (UGX)" },
  { code: "RWF", name: "Rwandan Franc (RWF)" },
  { code: "USD", name: "US Dollar (USD)" },
  { code: "ZAR", name: "South African Rand (ZAR)" },
  { code: "NGN", name: "Nigerian Naira (NGN)" },
  { code: "EUR", name: "Euro (EUR)" },
];

// ---------------------------------------------------------------
// WebM duration fix — Chrome's MediaRecorder can't know the final
// length of a recording until you call stop(), so it writes an
// "unknown" placeholder into the WebM container's
// Segment > Info > Duration field instead. That's exactly what makes
// video.duration read as Infinity (or an inaccurate, shifting
// estimate) when a viewer plays a replay back — see ReplayFeed.js's
// resolveRealDuration(), which works around it client-side, per
// video, on every playback.
//
// This fixes it once, at the source: right after a stream ends (see
// handleEndStream below), when the real elapsed recording time is
// finally known, patch it directly into the container before
// uploading. The Duration element Chrome writes is a fixed-width
// placeholder, so this only ever overwrites those existing bytes in
// place — no re-encoding, no resizing, nothing else in the file
// moves. If the container doesn't look the way this expects (a
// different browser's muxer, a missing Duration element, etc.) it
// just returns the original blob untouched and the client-side
// workaround in ReplayFeed.js still covers playback.
//
// This is a minimal EBML walk (Segment -> Info -> Duration), not a
// general WebM/Matroska parser — enough structure-reading to reach
// that one element and no more.
// ---------------------------------------------------------------

const EBML_ID_SEGMENT = 0x18538067;
const EBML_ID_INFO = 0x1549a966;
const EBML_ID_DURATION = 0x4489;
const EBML_ID_TIMECODE_SCALE = 0x2ad7b1;

// Reads one EBML variable-length integer at `offset`. Element IDs
// keep their leading length-marker bits as part of the ID's value
// (stripMarker: false); data sizes have the marker bits removed, and
// a value of all-1s (after stripping) means "unknown size"
// (stripMarker: true).
function readEbmlVint(view, offset, stripMarker) {
  if (offset >= view.byteLength) return null;
  const firstByte = view.getUint8(offset);
  let length = 1;
  let mask = 0x80;
  while (length <= 8 && !(firstByte & mask)) {
    length++;
    mask >>= 1;
  }
  if (length > 8 || offset + length > view.byteLength) return null;

  let value = stripMarker ? firstByte & (mask - 1) : firstByte;
  for (let i = 1; i < length; i++) {
    value = value * 256 + view.getUint8(offset + i);
  }

  const isUnknown = stripMarker && value === Math.pow(2, 7 * length) - 1;
  return { value, length, isUnknown };
}

// Scans the direct children of an EBML element (byte range [start,
// end)) for one with the given ID, returning its data's byte range.
// An "unknown size" child (only valid for the outermost Segment in a
// live-recorded file) is treated as running to the end of the range,
// since that's the only sane interpretation without a full Cues walk.
function findEbmlChild(view, start, end, targetId) {
  let offset = start;
  while (offset < end) {
    const idVint = readEbmlVint(view, offset, false);
    if (!idVint) return null;
    const elementId = idVint.value;
    offset += idVint.length;

    const sizeVint = readEbmlVint(view, offset, true);
    if (!sizeVint) return null;
    offset += sizeVint.length;

    const dataStart = offset;
    const dataSize = sizeVint.isUnknown ? end - dataStart : sizeVint.value;
    const dataEnd = dataStart + dataSize;
    if (dataEnd > end || dataEnd < dataStart) return null; // malformed / out of range

    if (elementId === targetId) {
      return { dataStart, dataEnd };
    }

    offset = dataEnd;
  }
  return null;
}

async function fixWebmDuration(blob, durationMs) {
  if (!blob || !blob.type?.includes("webm") || !Number.isFinite(durationMs) || durationMs <= 0) {
    return blob;
  }

  try {
    const buffer = await blob.arrayBuffer();
    const view = new DataView(buffer);

    const segment = findEbmlChild(view, 0, buffer.byteLength, EBML_ID_SEGMENT);
    if (!segment) return blob;

    const info = findEbmlChild(view, segment.dataStart, segment.dataEnd, EBML_ID_INFO);
    if (!info) return blob;

    // TimecodeScale defines what unit Duration is measured in
    // (nanoseconds per tick); per spec it defaults to 1,000,000 (1ms)
    // if the element is absent.
    let timecodeScale = 1000000;
    const timecodeScaleEl = findEbmlChild(view, info.dataStart, info.dataEnd, EBML_ID_TIMECODE_SCALE);
    if (timecodeScaleEl) {
      let raw = 0;
      for (let i = timecodeScaleEl.dataStart; i < timecodeScaleEl.dataEnd; i++) {
        raw = raw * 256 + view.getUint8(i);
      }
      if (raw > 0) timecodeScale = raw;
    }

    const durationEl = findEbmlChild(view, info.dataStart, info.dataEnd, EBML_ID_DURATION);
    if (!durationEl) {
      // No placeholder to overwrite — inserting one would resize the
      // container and shift every offset after it, which isn't worth
      // the complexity here. ReplayFeed.js's playback-side workaround
      // still covers this file.
      return blob;
    }

    const durationInScaleUnits = (durationMs * 1e6) / timecodeScale; // ms -> ns -> TimecodeScale units
    const byteLength = durationEl.dataEnd - durationEl.dataStart;

    if (byteLength === 8) {
      view.setFloat64(durationEl.dataStart, durationInScaleUnits);
    } else if (byteLength === 4) {
      view.setFloat32(durationEl.dataStart, durationInScaleUnits);
    } else {
      return blob; // unexpected width — don't risk writing a corrupt file
    }

    return new Blob([buffer], { type: blob.type });
  } catch (err) {
    console.error("Failed to patch WebM duration — uploading the replay unpatched:", err);
    return blob;
  }
}

export default function GoLiveControlPanel() {
  const navigate = useNavigate();
  const socket = useRef();
  const chatEndRef = useRef(null);

  const [currentUser, setCurrentUser] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [mode, setMode] = useState("setup"); // 'setup' | 'live'
  const [session, setSession] = useState(null);

  // ---- setup form state ----
  const [title, setTitle] = useState("");
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [streamMode, setStreamMode] = useState("image"); // 'image' | 'webrtc' | 'external'
  const [videoUrl, setVideoUrl] = useState("");
  const [currency, setCurrency] = useState("TZS");
  const [quickRepliesText, setQuickRepliesText] = useState(
    "Loved it! ❤️, Need this! 🔥, How much? 🤔"
  );
  const [surpriseMinutes, setSurpriseMinutes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // ---- live camera (LiveKit) state — only touched when streamMode
  // is 'webrtc'. The Room instance exists purely in memory for the
  // duration of the broadcast; nothing here is ever written to disk
  // or a database, and room.disconnect() on End Stream tears it all
  // down — LiveKit's server discards the room the moment its last
  // publisher leaves, and nothing was ever recorded to begin with. ----
  const roomRef = useRef(null);
  const [cameraError, setCameraError] = useState("");

  // ---- filter/beautify pipeline. We deliberately don't let LiveKit's
  // setCameraEnabled grab + publish the camera directly — instead this
  // hook acquires the camera itself, runs each frame through a canvas
  // (color filter + skin-smoothing), and we publish ITS output track
  // below via publishFilteredCamera. That keeps the filter pipeline as
  // the single source of truth for "what the camera looks like" —
  // viewers, the preview, and the 24-hour replay recording (which just
  // reads whatever track ends up published) all see the same result. ----
  const beautifyCam = useBeautifyCamera();
  const [editingFilters, setEditingFilters] = useState(false);

  // ---- client-side recording (MediaRecorder), for the 24-hour
  // replay. This records the SAME local tracks LiveKit is publishing
  // — nothing extra is captured, and nothing is sent anywhere until
  // handleEndStream explicitly uploads the finished file to Sanity.
  // Recording state is only ever held in memory (recordedChunksRef)
  // for the duration of the stream. ----
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  // Wall-clock time recording actually started — used to compute the
  // real elapsed duration when the stream ends, which is what gets
  // patched into the WebM container via fixWebmDuration above.
  const recordingStartedAtRef = useRef(null);
  const [recordingSupported, setRecordingSupported] = useState(true);

  // Picks the best mimeType this browser actually supports —
  // MediaRecorder support varies a fair amount (Safari in particular
  // historically only supports mp4, not webm). Returns null if
  // nothing usable is available, in which case recording is silently
  // skipped — going live must never depend on recording working.
  const getSupportedRecordingMimeType = () => {
    const candidates = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4",
    ];
    return candidates.find((type) => window.MediaRecorder?.isTypeSupported?.(type)) || null;
  };

  // Builds a plain MediaStream out of whatever video/audio tracks the
  // LiveKit Room's local participant is currently publishing, so
  // MediaRecorder can record exactly what viewers are seeing/hearing
  // — no separate getUserMedia call, no risk of it drifting from the
  // actual published stream. Since the published video track is now
  // the FILTERED canvas track (see publishFilteredCamera), the replay
  // picks up filters/beautify automatically — nothing to change here.
  const startClientRecording = useCallback((room) => {
    const mimeType = getSupportedRecordingMimeType();

    if (!mimeType) {
      console.warn("No supported MediaRecorder mimeType in this browser — skipping recording.");
      setRecordingSupported(false);
      return;
    }

    const videoPub = Array.from(room.localParticipant.videoTrackPublications.values())[0];
    const audioPub = Array.from(room.localParticipant.audioTrackPublications.values())[0];
    const tracks = [
      videoPub?.track?.mediaStreamTrack,
      audioPub?.track?.mediaStreamTrack,
    ].filter(Boolean);

    if (tracks.length === 0) {
      console.warn("No published tracks to record from.");
      setRecordingSupported(false);
      return;
    }

    try {
      const recordingStream = new MediaStream(tracks);
      const recorder = new MediaRecorder(recordingStream, { mimeType });

      recordedChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.start(1000);
      recordingStartedAtRef.current = Date.now();
      mediaRecorderRef.current = recorder;
      setRecordingSupported(true);
    } catch (err) {
      console.error("Failed to start client-side recording:", err);
      setRecordingSupported(false);
    }
  }, []);

  // Stops the recorder, waits for its final chunk to flush, and
  // returns the assembled Blob (or null if nothing was recording)
  // along with how long the recording actually ran — the latter is
  // what fixWebmDuration patches into the container before upload.
  const stopClientRecordingAndGetBlob = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      return Promise.resolve({ blob: null, durationMs: 0 });
    }

    // Captured now rather than after the async flush below, so it
    // reflects when recording actually stopped, not whenever the
    // final chunk happens to arrive a moment later.
    const durationMs = recordingStartedAtRef.current ? Date.now() - recordingStartedAtRef.current : 0;

    return new Promise((resolve) => {
      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: recorder.mimeType });
        recordedChunksRef.current = [];
        recordingStartedAtRef.current = null;
        resolve({ blob: blob.size > 0 ? blob : null, durationMs });
      };
      recorder.stop();
    });
  };

  // Fetches a signed, short-lived LiveKit access token from our own
  // backend (see live_livekit_server.js) — canPublish:true for the
  // host, scoped to exactly this session's room name (its Sanity _id).
  const fetchLiveKitToken = useCallback(
    async (roomName) => {
      const { data } = await apiClient.post(`${host}/api/live/token`, {
        roomName,
        identity: currentUser._id,
        name: currentUser.username,
        canPublish: true,
      });
      return data; // { token, url }
    },
    [currentUser]
  );

  // Acquires + filters the camera (via useBeautifyCamera) and publishes
  // its processed output to the given LiveKit room, in place of
  // room.localParticipant.setCameraEnabled(true). Audio is untouched —
  // still published the normal LiveKit way, since there's nothing to
  // filter there. Depends only on beautifyCam.start (itself a stable
  // useCallback) rather than the whole beautifyCam object, so this
  // doesn't change identity every render and re-trigger the effects
  // below.
  const publishFilteredCamera = useCallback(
    async (room) => {
      const processedTrack = await beautifyCam.start();
      const localVideoTrack = new LocalVideoTrack(processedTrack);
      await room.localParticipant.publishTrack(localVideoTrack, {
        source: Track.Source.Camera,
        simulcast: true,
      });
      await room.localParticipant.setMicrophoneEnabled(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [beautifyCam.start]
  );

  // ---- live dashboard state ----
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [viewerCount, setViewerCount] = useState(0);
  const [heartCount, setHeartCount] = useState(0);
  const [ending, setEnding] = useState(false);

  // ---- product picker state ----
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [hostProducts, setHostProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [pinning, setPinning] = useState(false);

  // ---- surprise timer edit ----
  const [editingSurprise, setEditingSurprise] = useState(false);
  const [surpriseEditMinutes, setSurpriseEditMinutes] = useState("");

  // ---------------------------------------------------------------
  // Load current user
  // ---------------------------------------------------------------
  useEffect(() => {
    const userData = localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY);
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // ---------------------------------------------------------------
  // Resume-safety: if this host already has an active session (e.g.
  // they refreshed mid-stream), skip setup and go straight back to
  // the live dashboard instead of letting them accidentally create a
  // second concurrent session.
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!currentUser?._id) return;

    const checkExisting = async () => {
      try {
        const existing = await client.fetch(
          `*[_type == "liveSession" && isLive == true && hostUserId == $uid] | order(startTime desc) [0]`,
          { uid: currentUser._id }
        );

        if (existing) {
          let featuredProduct = null;
          if (existing.featuredProductId) {
            featuredProduct = await client.fetch(
              `*[_id == $id][0]{ _id, name, price, currency, image, colors, slug }`,
              { id: existing.featuredProductId }
            );
          }

          // A LiveKit Room connection doesn't survive a page reload —
          // if this session is a live-camera one, reconnect now so
          // resuming after a refresh actually resumes broadcasting
          // instead of silently going dark while Sanity still shows
          // isLive: true.
          if (existing.streamMode === "webrtc") {
            try {
              const { token, url } = await fetchLiveKitToken(existing._id);
              const room = new Room();
              await room.connect(url, token);
              await publishFilteredCamera(room);
              roomRef.current = room;

              // Note: this necessarily starts a FRESH recording — the
              // in-browser MediaRecorder state from before the reload
              // is gone along with the rest of the JS heap. Whatever
              // was recorded pre-refresh isn't in this replay; only
              // the post-resume portion will be.
              startClientRecording(room);
            } catch (err) {
              console.error("Failed to reconnect live camera on resume:", err);
              setCameraError(
                "Reconnected to your live session, but camera access failed — grant permission and refresh to resume broadcasting."
              );
            }
          }

          setSession({ ...existing, featuredProduct });
          setMode("live");
        }
      } catch (err) {
        console.error("Failed to check for existing live session:", err);
      } finally {
        setCheckingExisting(false);
      }
    };

    checkExisting();
  }, [currentUser, startClientRecording, fetchLiveKitToken, publishFilteredCamera]);

  // ---------------------------------------------------------------
  // Socket connection once we're in live mode
  // ---------------------------------------------------------------
  useEffect(() => {
    if (mode !== "live" || !session?._id) return;

    socket.current = io(host);

    socket.current.emit("join-live-room", {
      sessionId: session._id,
      userId: currentUser?._id,
      username: currentUser?.username || "Host",
    });

    socket.current.on("live-chat-message", (msg) => {
      setMessages((prev) => [...prev, msg]);
    });

    socket.current.on("live-viewer-count", ({ count }) => {
      setViewerCount(count);
    });

    socket.current.on("live-heart-update", ({ count }) => {
      setHeartCount(count);
    });

    return () => {
      socket.current?.emit("leave-live-room", { sessionId: session._id });
      socket.current?.disconnect();
    };
  }, [mode, session?._id, currentUser]);

  // Disconnect the LiveKit room when the whole panel unmounts
  // (navigating away without explicitly ending the stream via the
  // button — e.g. a back-button press), so the camera/mic stop and
  // LiveKit's server discards the room rather than leaving a
  // publisher connection dangling. Also tears down the filter/canvas
  // pipeline's own camera acquisition — otherwise the raw getUserMedia
  // stream behind it would keep the camera light on after navigating
  // away, even though nothing is being published anymore.
  useEffect(() => {
    return () => {
      roomRef.current?.disconnect();
      roomRef.current = null;
      beautifyCam.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ---------------------------------------------------------------
  // Cover image selection (preview only — actual upload happens on
  // submit, same pattern as UploadForm.js's post-image handling)
  // ---------------------------------------------------------------
  const handleCoverImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverImageFile(file);
    setCoverImagePreview(URL.createObjectURL(file));
  };

  // ---------------------------------------------------------------
  // GO LIVE — creates the Sanity document and flips to live mode
  // ---------------------------------------------------------------
  const handleGoLive = async (e) => {
    e.preventDefault();
    setFormError("");
    setCameraError("");

    if (!title.trim()) {
      setFormError("Give your stream a title.");
      return;
    }

    setSubmitting(true);
    try {
      let coverImageAsset = null;
      if (coverImageFile) {
        const upload = await client.assets.upload("image", coverImageFile);
        coverImageAsset = {
          _type: "image",
          asset: { _ref: upload._id, _type: "reference" },
        };
      }

      const quickReplies = quickRepliesText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const surpriseUnboxingAt = surpriseMinutes
        ? new Date(Date.now() + Number(surpriseMinutes) * 60 * 1000).toISOString()
        : undefined;

      const doc = {
        _type: "liveSession",
        title: title.trim(),
        hostUserId: currentUser._id,
        hostName: currentUser.username,
        hostAvatarUrl: currentUser.avatarImage || null,
        coverImage: coverImageAsset,
        streamMode,
        videoUrl: streamMode === "external" ? videoUrl.trim() || undefined : undefined,
        isLive: true,
        startTime: new Date().toISOString(),
        surpriseUnboxingAt,
        quickReplies: quickReplies.length ? quickReplies : undefined,
        currency,
        seedViewerCount: 0,
      };

      const created = await client.create(doc);

      // Connect to LiveKit AFTER the doc exists — the room name is
      // the session's own _id, so there's nothing to connect to until
      // this point. If camera/mic access fails or the connection
      // can't be established, we roll back by deleting the just-created
      // doc rather than leaving a liveSession that claims isLive:true
      // with streamMode:'webrtc' but never actually broadcasts.
      if (streamMode === "webrtc") {
        try {
          const { token, url } = await fetchLiveKitToken(created._id);
          const room = new Room();
          await room.connect(url, token);
          await publishFilteredCamera(room);
          roomRef.current = room;

          // Start recording now that there's actually video/audio
          // being published — this is what makes the 24-hour replay
          // possible. Purely client-side (MediaRecorder), no server
          // call, no cost beyond the host's own device doing a second
          // encoding pass alongside the WebRTC one. Deliberately never
          // allowed to block going live — a browser that can't record
          // still lets the host broadcast, it just won't have a replay.
          startClientRecording(room);
        } catch (err) {
          console.error("Camera/LiveKit connection failed:", err);
          setCameraError(
            "Couldn't access your camera/microphone or connect to the live server. Check permissions and try again, or switch to a cover image."
          );
          await client.delete(created._id).catch(() => {});
          beautifyCam.stop();
          setSubmitting(false);
          return;
        }
      }

      // Notify followers — best-effort, never blocks going live even
      // if it fails (a host's stream shouldn't fail to start just
      // because the notification fan-out hiccupped).
      //
      // The follow graph lives in Sanity, not Mongo — getFollowersList
      // (already used in UploadForm.js/LiveDiscovery.js) resolves it
      // to a plain array of Mongo user IDs. The backend's only job is
      // matching those IDs against the User model for FCM tokens; it
      // doesn't know anything about follow relationships itself.
      getFollowersList(currentUser._id)
        .then((followerIds) =>
          apiClient.post(`${host}/api/live/notify-followers`, {
            hostUserId: currentUser._id,
            sessionId: created._id,
            title: title.trim(),
            followerIds,
          })
        )
        .catch((err) => console.error("Failed to notify followers:", err));

      setSession({ ...created, featuredProduct: null });
      setMode("live");
    } catch (err) {
      console.error("Failed to go live:", err);
      setFormError("Failed to start the stream. Please try again.");
      // Disconnect if we got as far as connecting to LiveKit but a
      // later step failed — no reason to leave the camera/mic
      // publishing for a session that never actually started.
      roomRef.current?.disconnect();
      roomRef.current = null;
      beautifyCam.stop();
      if (mediaRecorderRef.current?.state !== "inactive") {
        mediaRecorderRef.current?.stop();
      }
      mediaRecorderRef.current = null;
      recordedChunksRef.current = [];
      recordingStartedAtRef.current = null;
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------
  // Host reply in chat
  // ---------------------------------------------------------------
  const sendReply = () => {
    const trimmed = replyText.trim();
    if (!trimmed || !session?._id) return;

    socket.current?.emit("live-chat-message", {
      sessionId: session._id,
      userId: currentUser._id,
      username: currentUser.username,
      text: trimmed,
      isHost: true,
    });

    setReplyText("");
  };

  const handleReplyKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      sendReply();
    }
  };

  // ---------------------------------------------------------------
  // Product picker — search the host's own catalog across all types
  // ---------------------------------------------------------------
  const openProductPicker = useCallback(async () => {
    setShowProductPicker(true);
    setLoadingProducts(true);
    try {
      const results = await client.fetch(
        `*[_type in $types && user == $uid]{ _id, _type, name, price, currency, image, colors, slug }`,
        { types: PRODUCT_TYPES, uid: currentUser._id }
      );
      setHostProducts(results);
    } catch (err) {
      console.error("Failed to load host products:", err);
    } finally {
      setLoadingProducts(false);
    }
  }, [currentUser]);

  const filteredProducts = hostProducts.filter((p) =>
    p.name?.toLowerCase().includes(productSearch.toLowerCase())
  );

  const pinProduct = async (product) => {
    if (!session?._id) return;
    setPinning(true);
    try {
      await client.patch(session._id).set({ featuredProductId: product._id }).commit();
      setSession((prev) => ({ ...prev, featuredProduct: product, featuredProductId: product._id }));
      socket.current?.emit("live-pin-product", { sessionId: session._id, productId: product._id });
      setShowProductPicker(false);
    } catch (err) {
      console.error("Failed to pin product:", err);
    } finally {
      setPinning(false);
    }
  };

  const unpinProduct = async () => {
    if (!session?._id) return;
    try {
      await client.patch(session._id).unset(["featuredProductId"]).commit();
      setSession((prev) => ({ ...prev, featuredProduct: null, featuredProductId: null }));
      socket.current?.emit("live-pin-product", { sessionId: session._id, productId: null });
    } catch (err) {
      console.error("Failed to unpin product:", err);
    }
  };

  // ---------------------------------------------------------------
  // Surprise unboxing timer control
  // ---------------------------------------------------------------
  const applySurpriseTimer = async () => {
    if (!session?._id || !surpriseEditMinutes) return;
    const surpriseUnboxingAt = new Date(
      Date.now() + Number(surpriseEditMinutes) * 60 * 1000
    ).toISOString();

    try {
      await client.patch(session._id).set({ surpriseUnboxingAt }).commit();
      setSession((prev) => ({ ...prev, surpriseUnboxingAt }));
      socket.current?.emit("live-surprise-update", { sessionId: session._id, surpriseUnboxingAt });
      setEditingSurprise(false);
      setSurpriseEditMinutes("");
    } catch (err) {
      console.error("Failed to update surprise timer:", err);
    }
  };

  // ---------------------------------------------------------------
  // End stream
  // ---------------------------------------------------------------
  const handleEndStream = async (auto = false) => {
    if (!session?._id || ending) return;

    if (!auto) {
      const confirmed = window.confirm("End the live stream? Viewers will be disconnected.");
      if (!confirmed) return;
    }

    setEnding(true);
    try {
      await client
        .patch(session._id)
        .set({ isLive: false, endTime: new Date().toISOString() })
        .commit();

      socket.current?.emit("live-end-session", { sessionId: session._id });

      // Disconnect from LiveKit — this stops publishing camera/mic
      // immediately. If a client-side recording was running (see
      // startClientRecording in handleGoLive), stop it FIRST so the
      // very last moments of the stream are captured cleanly, then
      // disconnect the room, then hand the finished Blob off to
      // Sanity in the background — this is the entire mechanism
      // behind the 24-hour replay, no server round-trip needed to
      // start or stop it.
      if (session.streamMode === "webrtc") {
        const { blob: recordingBlob, durationMs } = await stopClientRecordingAndGetBlob();

        roomRef.current?.disconnect();
        roomRef.current = null;
        beautifyCam.stop();

        if (recordingBlob) {
          // Patch the real duration into the container now that it's
          // known — see fixWebmDuration above for why this needs to
          // happen here rather than relying solely on ReplayFeed.js's
          // playback-side workaround.
          const patchedBlob = await fixWebmDuration(recordingBlob, durationMs);
          const fileExtension = patchedBlob.type.includes("mp4") ? "mp4" : "webm";
          client.assets
            .upload("file", patchedBlob, {
              filename: `live-${session._id}.${fileExtension}`,
              contentType: patchedBlob.type,
            })
            .then((asset) =>
              client
                .patch(session._id)
                .set({
                  recordingAsset: { _type: "file", asset: { _type: "reference", _ref: asset._id } },
                  recordingExpiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
                })
                .commit()
            )
            .catch((err) => console.error("Failed to upload/save replay (stream still ended fine):", err));
        }

        // Explicitly terminate the room server-side too, so any
        // viewer still connected gets disconnected immediately rather
        // than waiting for their own connection to notice the
        // publisher is gone.
        apiClient
          .post(`${host}/api/live/end-room`, { roomName: session._id })
          .catch((err) => console.error("Failed to end LiveKit room server-side:", err));
      }

      socket.current?.disconnect();

      if (auto) {
        window.alert("Your live stream reached the 1-hour limit and has ended automatically.");
      }

      navigate("/");
    } catch (err) {
      console.error("Failed to end stream:", err);
      setEnding(false);
    }
  };

  // ---------------------------------------------------------------
  // Auto-end after 1 hour. Keyed off session.startTime (not "time
  // since this page loaded") so it's correct even after a host
  // refreshes mid-stream — resuming a 40-minute-old session still
  // only gets 20 more minutes, not a fresh hour.
  // ---------------------------------------------------------------
  const [autoEndMsRemaining, setAutoEndMsRemaining] = useState(null);

  useEffect(() => {
    if (mode !== "live" || !session?.startTime) return;

    const endAtMs = new Date(session.startTime).getTime() + AUTO_END_DURATION_MS;

    const tick = () => {
      const remaining = endAtMs - Date.now();
      setAutoEndMsRemaining(Math.max(remaining, 0));
      if (remaining <= 0) {
        handleEndStream(true);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, session?.startTime]);

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------

  if (checkingExisting) {
    return (
      <Container>
        <CenterMessage>Checking for an active session…</CenterMessage>
      </Container>
    );
  }

  if (mode === "setup") {
    return (
      <Container light>
        <SetupHeader>
          <FiX size={22} onClick={() => navigate(-1)} style={{ cursor: "pointer" }} />
          <h2>Go Live Brand Your Business</h2>
          <div style={{ width: 22 }} />
        </SetupHeader>

        <SetupForm onSubmit={handleGoLive}>
          <Label>Stream title</Label>
          <TextInput
            placeholder="e.g. Unboxing new arrivals from Kariakoo!"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <Label>Cover image</Label>
          <CoverUploadWrapper>
            {coverImagePreview ? (
              <CoverPreview src={coverImagePreview} alt="Cover preview" />
            ) : (
              <CoverPlaceholder>No image selected</CoverPlaceholder>
            )}
            <input
              id="cover-image-upload"
              type="file"
              accept="image/*"
              onChange={handleCoverImageChange}
              style={{ display: "none" }}
            />
            <UploadButton htmlFor="cover-image-upload">Choose Image</UploadButton>
          </CoverUploadWrapper>

          <Label>Stream type</Label>
          <StreamModeRow>
            <StreamModeOption
              type="button"
              selected={streamMode === "image"}
              onClick={() => setStreamMode("image")}
            >
              Cover Image
            </StreamModeOption>
            <StreamModeOption
              type="button"
              selected={streamMode === "webrtc"}
              onClick={() => setStreamMode("webrtc")}
            >
              📹 Live Camera
            </StreamModeOption>
            <StreamModeOption
              type="button"
              selected={streamMode === "external"}
              onClick={() => setStreamMode("external")}
            >
              External URL
            </StreamModeOption>
          </StreamModeRow>

          {streamMode === "webrtc" && (
            <>
              <HelperText>
                Broadcasts your camera and mic live to viewers. A replay is saved automatically
                and deleted 24 hours after you end the stream, just like a status update — keep
                this tab open for the whole broadcast so the replay records properly. You'll be
                asked for camera/mic permission when you tap Go Live below. Once live, you can
                apply a filter and adjust beautification from the ✨ button on the dashboard.
              </HelperText>
              {!getSupportedRecordingMimeType() && (
                <HelperText style={{ color: "#dc2626" }}>
                  Heads up: this browser can't save a replay of your stream. You can still go
                  live — viewers just won't have anything to watch back afterward.
                </HelperText>
              )}
            </>
          )}

          {streamMode === "external" && (
            <>
              <Label>Stream URL</Label>
              <TextInput
                placeholder="https://your-stream-provider.com/..."
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
              />
              <HelperText>HLS (.m3u8) or MP4 URL from a provider like Mux or IVS.</HelperText>
            </>
          )}

          {cameraError && <ErrorText>{cameraError}</ErrorText>}

          <Label>Currency</Label>
          <SelectInput value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCY_OPTIONS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code}
              </option>
            ))}
          </SelectInput>

          <Label>Quick reply chips (comma-separated)</Label>
          <TextInput
            value={quickRepliesText}
            onChange={(e) => setQuickRepliesText(e.target.value)}
          />

          <Label>Surprise unboxing — minutes from now (optional)</Label>
          <TextInput
            type="number"
            min="1"
            placeholder="e.g. 15"
            value={surpriseMinutes}
            onChange={(e) => setSurpriseMinutes(e.target.value)}
          />

          {formError && <ErrorText>{formError}</ErrorText>}

          <GoLiveButton type="submit" disabled={submitting}>
            {submitting
              ? streamMode === "webrtc"
                ? "Requesting camera…"
                : "Starting…"
              : "🔴 Go Live"}
          </GoLiveButton>
        </SetupForm>
      </Container>
    );
  }

  // ---- mode === 'live' ----
  return (
    <FullscreenContainer>
      {/* ---------- BACKGROUND: host's own camera, full screen ----------
          For webrtc streams this is the CANVAS the filter/beautify
          pipeline draws into (see useBeautifyCamera) — not a raw
          <video> — since that canvas's output is also exactly what
          gets published to LiveKit and recorded for the replay. */}
      {session?.streamMode === "webrtc" ? (
        cameraError ? (
          <FullscreenFallback />
        ) : (
          <FullscreenCanvasContainer
            mirrored={beautifyCam.facingMode === "user"}
            ref={(el) => {
              const canvasEl = beautifyCam.canvasRef.current;
              if (el && canvasEl && el.firstChild !== canvasEl) {
                el.innerHTML = "";
                el.appendChild(canvasEl);
              }
            }}
          />
        )
      ) : session?.coverImage ? (
        <FullscreenImage src={urlFor(session.coverImage)} alt={session.title} />
      ) : (
        <FullscreenFallback />
      )}
      <Scrim />

      {cameraError && <CameraErrorBanner>{cameraError}</CameraErrorBanner>}

      {/* ---------- TOP OVERLAY: live status, stats, auto-end, end button ---------- */}
      <TopOverlayRow>
        <LiveIndicator>
          <FiRadio size={14} />
          LIVE
        </LiveIndicator>
        <StatPill>
          <AiOutlineEye size={14} /> {viewerCount}
        </StatPill>
        <StatPill>
          <AiFillHeart size={14} color="#f97316" /> {heartCount}
        </StatPill>
        {autoEndMsRemaining !== null && (
          <AutoEndChip warning={autoEndMsRemaining < 5 * 60 * 1000}>
            {formatCountdown(autoEndMsRemaining)}
          </AutoEndChip>
        )}
        {session?.streamMode === "webrtc" && !cameraError && (
          <TopIconButton
            onClick={() => beautifyCam.switchCamera()}
            disabled={beautifyCam.switchingCamera}
            title="Switch camera"
          >
            <FiRefreshCw size={14} />
          </TopIconButton>
        )}
        <EndButton onClick={() => handleEndStream(false)} disabled={ending}>
          {ending ? "Ending…" : "End"}
        </EndButton>
      </TopOverlayRow>

      {session?.streamMode === "webrtc" && (
        <RecordingStatusNote recording={recordingSupported}>
          {recordingSupported ? "● Recording replay" : "No replay this time"}
        </RecordingStatusNote>
      )}

      {/* ---------- FLOATING PRODUCT PIN ---------- */}
      <PinnedProductMini onClick={openProductPicker}>
        {session?.featuredProduct ? (
          <>
            {session.featuredProduct.image?.[0] && (
              <PinnedProductMiniImage src={urlFor(session.featuredProduct.image[0])} alt="" />
            )}
            <PinnedProductMiniText>
              <strong>{session.featuredProduct.name}</strong>
              <span>
                {session.featuredProduct.currency || currency} {session.featuredProduct.price?.toLocaleString()}
              </span>
            </PinnedProductMiniText>
            <UnpinMiniButton
              onClick={(e) => {
                e.stopPropagation();
                unpinProduct();
              }}
            >
              <FiX size={12} />
            </UnpinMiniButton>
          </>
        ) : (
          <PinnedProductMiniText>
            <FiPackage size={14} />
            <span>Pin a product</span>
          </PinnedProductMiniText>
        )}
      </PinnedProductMini>

      {/* ---------- FLOATING SURPRISE-TIMER BUTTON ---------- */}
      <SurpriseFloatingButton onClick={() => setEditingSurprise((v) => !v)} active={!!session?.surpriseUnboxingAt}>
        <FiGift size={18} />
      </SurpriseFloatingButton>

      {editingSurprise && (
        <SurpriseMiniPanel onClick={(e) => e.stopPropagation()}>
          {session?.surpriseUnboxingAt && (
            <EmptyNote style={{ color: "#ddd" }}>
              Currently set for {new Date(session.surpriseUnboxingAt).toLocaleTimeString()}
            </EmptyNote>
          )}
          <ReplyRow>
            <TextInput
              type="number"
              min="1"
              placeholder="Minutes from now"
              value={surpriseEditMinutes}
              onChange={(e) => setSurpriseEditMinutes(e.target.value)}
              style={{ marginBottom: 0 }}
            />
            <SendReplyButton onClick={applySurpriseTimer} disabled={!surpriseEditMinutes}>
              Set
            </SendReplyButton>
          </ReplyRow>
        </SurpriseMiniPanel>
      )}

      {/* ---------- FLOATING FILTER/BEAUTIFY BUTTON (webrtc only) ----------
          Mirrors the surprise-timer button's pattern, on the opposite
          side, so it stays out of the way of chat/product/surprise UI. */}
      {session?.streamMode === "webrtc" && !cameraError && (
        <>
          <FilterFloatingButton onClick={() => setEditingFilters((v) => !v)} active={editingFilters}>
            <FiSliders size={18} />
          </FilterFloatingButton>

          {editingFilters && (
            <FilterMiniPanel onClick={(e) => e.stopPropagation()}>
              <FilterSelector
                filterId={beautifyCam.filterId}
                onFilterChange={beautifyCam.setFilterId}
                beautify={beautifyCam.beautify}
                onBeautifyChange={beautifyCam.setBeautify}
              />
            </FilterMiniPanel>
          )}
        </>
      )}

      {/* ---------- CHAT OVERLAY — starts at the vertical midpoint,
          scrolls upward as new messages arrive, sits above the reply
          input at the very bottom ---------- */}
      <ChatOverlay>
        {messages.length === 0 && <EmptyNote style={{ color: "#ccc" }}>No messages yet.</EmptyNote>}
        {messages.map((msg) => (
          <FeedBubble key={msg._id} isHost={msg.isHost}>
            <FeedRow>
              <FeedName isHost={msg.isHost}>
                {msg.username}
                {msg.isHost && <HostTag>HOST</HostTag>}
              </FeedName>
              <FeedTime>
                {new Date(msg.timestamp).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </FeedTime>
            </FeedRow>
            <FeedText>{msg.text}</FeedText>
          </FeedBubble>
        ))}
        <div ref={chatEndRef} />
      </ChatOverlay>

      {/* ---------- REPLY INPUT, pinned to the very bottom ---------- */}
      <ReplyInputRow>
        <TextInput
          placeholder="Reply to your viewers..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          onKeyDown={handleReplyKeyDown}
          style={{ marginBottom: 0 }}
        />
        <SendReplyButton onClick={sendReply} disabled={!replyText.trim()}>
          <FiSend size={16} />
        </SendReplyButton>
      </ReplyInputRow>

      {/* ---- PRODUCT PICKER MODAL ---- */}
      {showProductPicker && (
        <ModalBackdrop onClick={() => setShowProductPicker(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h4>Pin a Product</h4>
              <FiX size={20} onClick={() => setShowProductPicker(false)} style={{ cursor: "pointer" }} />
            </ModalHeader>
            <TextInput
              placeholder="Search your products..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
            />
            <ProductGrid>
              {loadingProducts ? (
                <EmptyNote>Loading your products…</EmptyNote>
              ) : filteredProducts.length === 0 ? (
                <EmptyNote>No products found.</EmptyNote>
              ) : (
                filteredProducts.map((p) => (
                  <ProductGridItem key={p._id} onClick={() => !pinning && pinProduct(p)}>
                    {p.image?.[0] && <ProductGridImage src={urlFor(p.image[0])} alt={p.name} />}
                    <ProductGridName>{p.name}</ProductGridName>
                    <ProductGridPrice>
                      {p.currency} {p.price?.toLocaleString()}
                    </ProductGridPrice>
                  </ProductGridItem>
                ))
              )}
            </ProductGrid>
          </ModalCard>
        </ModalBackdrop>
      )}
    </FullscreenContainer>
  );
}

/* ================= STYLES ================= */

const Container = styled.div`
  min-height: 100vh;
  background: ${(props) => (props.light ? "#f8f9fb" : "#000")};
  display: flex;
  flex-direction: column;
`;

const CenterMessage = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
`;

const SetupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  background: white;
  border-bottom: 1px solid #eee;

  h2 {
    margin: 0;
    font-size: 18px;
  }
`;

const SetupForm = styled.form`
  padding: 18px;
  display: flex;
  flex-direction: column;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #444;
  margin: 14px 0 6px;
`;

const TextInput = styled.input`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #f97316;
  }
`;

const SelectInput = styled.select`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
`;

const HelperText = styled.p`
  font-size: 11px;
  color: #999;
  margin: 4px 0 0;
`;

const ErrorText = styled.p`
  color: #dc2626;
  font-size: 12px;
  margin-top: 10px;
`;

const StreamModeRow = styled.div`
  display: flex;
  gap: 8px;
`;

const StreamModeOption = styled.button`
  flex: 1;
  background: ${(props) => (props.selected ? "#f97316" : "#f1f1f1")};
  color: ${(props) => (props.selected ? "white" : "#333")};
  border: none;
  border-radius: 8px;
  padding: 10px 4px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
`;

const FullscreenContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background: #000;
`;

const FullscreenCanvasContainer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  /* Mirror for the front camera only, so the host sees themselves the
     way a mirror would; the rear camera should show the natural,
     un-mirrored view since that's what it's pointed away from you. */
  transform: ${(props) => (props.mirrored ? "scaleX(-1)" : "none")};
`;

const FullscreenImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FullscreenFallback = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(160deg, #1f2937, #111827);
`;

const Scrim = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.05) 25%, rgba(0, 0, 0, 0.1) 60%, rgba(0, 0, 0, 0.6) 100%);
`;

const CameraErrorBanner = styled.div`
  position: absolute;
  top: 50%;
  left: 20px;
  right: 20px;
  transform: translateY(-50%);
  z-index: 5;
  background: rgba(220, 38, 38, 0.9);
  color: white;
  padding: 12px;
  border-radius: 8px;
  font-size: 13px;
  text-align: center;
`;

const TopOverlayRow = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
  right: 14px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LiveIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: #f97316;
  color: white;
  font-weight: 700;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 20px;
`;

const AutoEndChip = styled.div`
  display: flex;
  align-items: center;
  background: ${(props) => (props.warning ? "rgba(220, 38, 38, 0.85)" : "rgba(0, 0, 0, 0.45)")};
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
`;

const RecordingStatusNote = styled.p`
  position: absolute;
  top: 54px;
  left: 14px;
  z-index: 5;
  font-size: 10px;
  font-weight: 600;
  color: ${(props) => (props.recording ? "#f87171" : "#ccc")};
  background: rgba(0, 0, 0, 0.4);
  padding: 3px 8px;
  border-radius: 10px;
  margin: 0;
`;

const PinnedProductMini = styled.div`
  position: absolute;
  right: 14px;
  bottom: 50%;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 6px 10px;
  max-width: 200px;
  cursor: pointer;
`;

const PinnedProductMiniImage = styled.img`
  width: 42px;
  height: 42px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
`;

const PinnedProductMiniText = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-size: 11px;
  color: #111;

  strong {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    color: #f97316;
    font-size: 10px;
  }
`;

const UnpinMiniButton = styled.button`
  background: #f1f1f1;
  border: none;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
`;

const SurpriseFloatingButton = styled.button`
  position: absolute;
  right: 14px;
  bottom: 92px;
  z-index: 5;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${(props) => (props.active ? "#f97316" : "rgba(0, 0, 0, 0.45)")};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SurpriseMiniPanel = styled.div`
  position: absolute;
  right: 14px;
  bottom: 138px;
  z-index: 6;
  width: 220px;
  background: rgba(20, 20, 20, 0.92);
  border: 1px solid #f97316;
  border-radius: 10px;
  padding: 10px;
`;

const FilterFloatingButton = styled.button`
  position: absolute;
  right: 14px;
  bottom: 40%;
  z-index: 5;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: ${(props) => (props.active ? "#f97316" : "rgba(0, 0, 0, 0.45)")};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const TopIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  color: white;
  border: none;
  border-radius: 20px;
  width: 28px;
  height: 28px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const FilterMiniPanel = styled.div`
  position: absolute;
  left: 14px;
  bottom: 138px;
  z-index: 6;
  width: 260px;
  border: 1px solid #f97316;
  border-radius: 14px;
  overflow: hidden;
`;

const ChatOverlay = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  top: 50%; /* chat scrolls starting from the vertical midpoint down to the reply row */
  bottom: 72px;
  z-index: 4;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 6px;
`;

const ReplyInputRow = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 14px;
  z-index: 5;
  display: flex;
  gap: 8px;
`;

const CoverUploadWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CoverPreview = styled.img`
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 10px;
`;

const CoverPlaceholder = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 10px;
  background: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  color: #999;
  text-align: center;
  padding: 4px;
`;

const UploadButton = styled.label`
  background: #f1f1f1;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 12px;
  cursor: pointer;
`;

const GoLiveButton = styled.button`
  margin-top: 26px;
  background: ${(props) => (props.disabled ? "#0f1f3d" : "#f97316")};
  color: white;
  border: none;
  border-radius: 10px;
  padding: 14px;
  font-size: 15px;
  font-weight: 700;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
`;

const StatPill = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.45);
  color: white;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
`;

const EndButton = styled.button`
  margin-left: auto;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 700;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
`;

const EmptyNote = styled.p`
  color: #999;
  font-size: 12px;
`;

const FeedBubble = styled.div`
  background: ${(props) => (props.isHost ? "rgba(37, 99, 235, 0.75)" : "rgba(0, 0, 0, 0.5)")};
  color: white;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  max-width: 90%;
`;

const FeedRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const FeedName = styled.span`
  font-weight: 700;
  font-size: 11px;
  color: ${(props) => (props.isHost ? "#fef3c7" : "#f5f5f5")};
`;

const HostTag = styled.span`
  background: #f97316;
  color: white;
  font-size: 9px;
  padding: 1px 6px;
  border-radius: 8px;
  margin-left: 6px;
`;

const FeedTime = styled.span`
  font-size: 10px;
  color: #ccc;
`;

const FeedText = styled.div`
  margin-top: 2px;
  color: white;
`;

const ReplyRow = styled.div`
  display: flex;
  gap: 8px;
`;

const SendReplyButton = styled.button`
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0 14px;
  font-size: 12px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px;
  width: 90%;
  max-width: 420px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  h4 {
    margin: 0;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 12px;
`;

const ProductGridItem = styled.div`
  cursor: pointer;
  text-align: center;
`;

const ProductGridImage = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 8px;
`;

const ProductGridName = styled.p`
  font-size: 10px;
  margin: 4px 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProductGridPrice = styled.p`
  font-size: 10px;
  color: #f97316;
  margin: 2px 0 0;
`;
